import { invalidate } from '@react-three/fiber';
import { type RefObject, useCallback, useEffect, useRef } from 'react';

import {
    applyCameraView,
    captureCameraView,
    isCameraView
} from 'app/modules/wallforming/components/scenes/camera-controls/utils/cameraView';

import type { CameraControls } from '@react-three/drei';

export const usePersistentPreviewCamera = (
    controlsRef: RefObject<CameraControls | null>,
    storageKey: string,
    subjectId: string | null
) => {
    const initializedSubject = useRef<string | null>(null);

    const restorePreviewCamera = useCallback(() => {
        const controls = controlsRef.current;
        if (!controls || subjectId === null) {
            return false;
        }
        if (initializedSubject.current === subjectId) {
            return true;
        }

        const isInitialSubject = initializedSubject.current === null;
        initializedSubject.current = subjectId;
        if (!isInitialSubject) {
            return false;
        }

        try {
            const raw = sessionStorage.getItem(storageKey);
            if (!raw) {
                return false;
            }
            const saved: unknown = JSON.parse(raw);
            if (
                !saved ||
                typeof saved !== 'object' ||
                !('subjectId' in saved) ||
                !('view' in saved) ||
                typeof saved.subjectId !== 'string' ||
                !isCameraView(saved.view)
            ) {
                throw new Error('Invalid saved preview camera view');
            }
            if (saved.subjectId !== subjectId) {
                return false;
            }
            applyCameraView(controls, saved.view, null);
            invalidate();
            return true;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to restore preview camera view', error);
            return false;
        }
    }, [controlsRef, storageKey, subjectId]);

    const savePreviewCamera = useCallback(() => {
        const controls = controlsRef.current;
        if (!controls || subjectId === null || initializedSubject.current !== subjectId) {
            return;
        }
        try {
            sessionStorage.setItem(storageKey, JSON.stringify({ subjectId, view: captureCameraView(controls) }));
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to save preview camera view', error);
        }
    }, [controlsRef, storageKey, subjectId]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                savePreviewCamera();
            }
        };

        window.addEventListener('pagehide', savePreviewCamera);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            window.removeEventListener('pagehide', savePreviewCamera);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [savePreviewCamera]);

    return { restorePreviewCamera, savePreviewCamera };
};
